import { getCurrentUser } from '../DatabaseAPI';
import { describeError, redactForLog } from './authLogging';

const STORAGE_KEY = 'authenticatedUser';

/**
 * Global API error handler for authentication issues
 * Should be called by all API functions when they encounter errors
 */
export const handleApiAuthError = (error, response = null) => {
  // The raw error and response can carry the submitted credentials and the full
  // user payload; log only their redacted shape.
  console.log('authUtils: Handling API auth error', describeError(error), redactForLog(response));
  
  // Check for authentication errors
  if (error.status === 401 || error.status === 403) {
    console.log('authUtils: Authentication error detected, clearing cached auth');
    
    // Clear cached authentication
    localStorage.removeItem(STORAGE_KEY);
    
    // Clear session storage
    sessionStorage.removeItem('selectedTeamId');
    sessionStorage.removeItem('selectedLeagueId');
    sessionStorage.removeItem('previousLocation');
    sessionStorage.removeItem('selectedPlayerId');
    
    // Redirect to login - using window.location to force full page reload
    // This ensures the app restarts with fresh authentication state
    console.log('authUtils: Redirecting to login due to auth error');
    window.location.href = '/';
    
    return true; // Indicates auth error was handled
  }
  
  return false; // Not an auth error
};

/**
 * Validate cached user with backend
 * Returns user data if valid, null if invalid
 */
export const validateCachedUser = async () => {
  try {
    const cachedUser = localStorage.getItem(STORAGE_KEY);
    
    if (!cachedUser) {
      return null;
    }
    
    console.log('authUtils: Validating cached user with backend...');
    
    // Call backend to validate current session
    const currentUser = await getCurrentUser();
    
    if (currentUser && currentUser.id) {
      console.log('authUtils: Cached user is valid');
      
      // Add starts_in_debug from user profile if available
      const userWithDebugPref = {
        ...currentUser,
        starts_in_debug: currentUser.mythic_profile?.starts_in_debug || false
      };
      
      // Update localStorage with fresh user data
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userWithDebugPref));
      
      return userWithDebugPref;
    } else {
      console.log('authUtils: Cached user is invalid');
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  } catch (error) {
    console.error('authUtils: Error validating cached user', describeError(error));
    
    // Handle auth errors
    if (handleApiAuthError(error)) {
      return null;
    }
    
    // For other errors, clear cache to be safe
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

/**
 * Store user authentication in localStorage
 */
export const storeAuthUser = (user) => {
  console.log('authUtils: Storing authenticated user');
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
};

/**
 * Get cached user from localStorage
 */
export const getCachedUser = () => {
  try {
    const cachedUser = localStorage.getItem(STORAGE_KEY);
    return cachedUser ? JSON.parse(cachedUser) : null;
  } catch (error) {
    console.error('authUtils: Error parsing cached user', describeError(error));
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  console.log('authUtils: Clearing all authentication data');
  
  // Clear localStorage
  localStorage.removeItem(STORAGE_KEY);
  
  // Clear sessionStorage
  sessionStorage.removeItem('selectedTeamId');
  sessionStorage.removeItem('selectedLeagueId');
  sessionStorage.removeItem('previousLocation');
  sessionStorage.removeItem('selectedPlayerId');
};

/**
 * Check if user should go to debug UI
 */
export const shouldUseDebugUI = (user) => {
  if (!user) return false;
  
  // Check if user has debug preference
  if (user.starts_in_debug === true) {
    console.log('authUtils: User has debug UI preference enabled');
    return true;
  }
  
  // Check for development environment
  if (process.env.NODE_ENV === 'development') {
    // You could add additional dev-specific logic here
    console.log('authUtils: Development environment detected');
  }
  
  // Check for debug query parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('dev') === 'true') {
    console.log('authUtils: Debug query parameter detected');
    return true;
  }
  
  return false;
};

/**
 * Enhanced API call wrapper with auth error handling
 * Use this to wrap API calls that need authentication
 */
export const apiCallWithAuth = async (apiFunction, ...args) => {
  try {
    const result = await apiFunction(...args);
    return result;
  } catch (error) {
    // Handle authentication errors
    if (handleApiAuthError(error)) {
      // Auth error was handled (user redirected), throw to stop execution
      throw new Error('Authentication failed - user redirected to login');
    }
    
    // Re-throw other errors for normal handling
    throw error;
  }
};